/**
 * "Schedule meeting" from a conversation (TRUS-691): the call button's split
 * menu and the composer menu both open the same dialog. Staff only, and only
 * when the org has meetings on — otherwise `open` is undefined and neither
 * entry point renders.
 */

import React, { Suspense, useCallback, useState } from 'react';
import type { ChannelMember } from '@/services/messagingApi';
import { LazyScheduleMeetingDialog } from './lazyDialog';
import { useCanScheduleMeetings } from './useMeetings';

interface UseMeetingSchedulerArgs {
  channelId: number;
  channelName?: string;
  channelMembers?: ChannelMember[];
  /** A member (portal participant) is viewing: never offered. */
  viewerIsMember?: boolean;
}

export function useMeetingScheduler({ channelId, channelName, channelMembers, viewerIsMember = false }: UseMeetingSchedulerArgs) {
  const canSchedule = useCanScheduleMeetings() && !viewerIsMember;
  const [isOpen, setOpen] = useState(false);
  const open = useCallback(() => setOpen(true), []);
  const dialog = canSchedule && isOpen ? (
    <Suspense fallback={null}>
      <LazyScheduleMeetingDialog
        open={isOpen}
        onOpenChange={setOpen}
        channelId={channelId}
        channelName={channelName}
        channelMembers={channelMembers}
      />
    </Suspense>
  ) : null;
  return { open: canSchedule ? open : undefined, dialog };
}
